import {combineLatest, switchMap, NEVER} from 'rxjs';
import {filter, map, scan, debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {genDownTrend, genUpTrend} from '../modules/components/calc';
import {url$} from './url';
import {currentSpotMarket$} from './market-spot';
import {market$} from './market-contract';

interface Acc {
  title: string;
  oldPriceEp: number;
  upTrend: boolean;
  downTrend: boolean;
}

const titlePrefix$ = url$.pipe(
  filter(Boolean),
  switchMap((url: Url) => {
    if (url.tradeType === 'Spot') {
      return currentSpotMarket$;
    }
    if (url.isPerpetual) {
      return market$;
    }
    return NEVER;
  }),
  map((market: SpotMarket | ContractMarket) => ({priceEp: market.closeEp, price: market.close} as PriceEntity)),
  distinctUntilChanged((p: PriceEntity, c: PriceEntity) => p.priceEp === c.priceEp),
  scan(accumulator, {
    title: '',
    oldPriceEp: NaN,
    upTrend: false,
    downTrend: false,
  }),
  map((acc: Acc) => acc.title)
);

const titleSuffix$ = url$.pipe(
  filter(Boolean),
  map((url: Url) => {
    if (url.tradeType === 'Spot') {
      return convertSpotTitleSuffix(url.tradePair as SpotPair);
    }
    if (url.isPerpetual) {
      return convertContractTitleSuffix(url.tradePair as ContractPair);
    }
    return '';
  })
);

export const docTitle$ = combineLatest([titlePrefix$, titleSuffix$]).pipe(
  map((titles) => titles.join(' ')),
  distinctUntilChanged(),
  debounceTime(100)
);

export const docDesc$ = url$.pipe(
  filter(Boolean),
  map((url: Url) => {
    if (url.tradeType === 'Spot') {
      return convertSpotDesc(url.tradePair as SpotPair);
    }
    if (url.isPerpetual) {
      return convertContractDesc(url.tradePair as ContractPair);
    }
    return '';
  }),
  debounceTime(100)
);

function convertSpotDesc(spot: SpotPair) {
  return `Real time ${spot.name} (${spot.displayBaseCurrency}) to ${spot.quoteName} (${spot.quoteCurrency}) chart, ${spot.pairName} Tradingview. Trade ${spot.displaySymbol} on Phemex now!`;
}

function convertContractDesc(contract: ContractPair) {
  return `Start trading ${contract.baseCurrencyName} futures on Phemex. ${contract.baseCurrencyName} futures trading charts in real time. Trade ${contract.baseCurrency}/USD perpetual futures with up to 100x leverage.`;
}

function accumulator(p: Acc, {priceEp, price}: PriceEntity): Acc {
  const upTrend = genUpTrend(p.oldPriceEp, priceEp, p.upTrend);
  const downTrend = genDownTrend(p.oldPriceEp, priceEp, p.downTrend);
  const oldPriceEp = priceEp;
  const title = `${upTrend ? '▲' : downTrend ? '▼' : ' '} ${price}`;
  return {
    title,
    upTrend,
    downTrend,
    oldPriceEp,
  };
}

function convertSpotTitleSuffix(spot: SpotPair) {
  return `${spot.pairName} | ${spot.displayBaseCurrency} to ${spot.quoteCurrency} Chart | Phemex Spot Trading`;
}

function convertContractTitleSuffix(contract: ContractPair) {
  return `${contract.pairName} Perpetual | ${contract.baseCurrency} Margin Trading | Phemex`;
}
